import {
  HERO_ARTICLE,
  LATEST_ARTICLES,
  LJ_CATEGORIES,
  POPULAR_ARTICLES,
  RECOMMENDED_ARTICLES,
  type LjArticle,
} from "./fixtures";

/**
 * Everything LJ can be searched over.
 *
 * The masthead has carried a search icon since the vertical shipped and there
 * was no page behind it — QA: "missing search page". Same pool the category
 * listings compose from, deduplicated by title.
 */
const POOL: LjArticle[] = (() => {
  const seen = new Set<string>();
  return [
    HERO_ARTICLE,
    ...LATEST_ARTICLES,
    ...RECOMMENDED_ARTICLES,
    ...POPULAR_ARTICLES,
  ].filter((a) => !seen.has(a.title) && seen.add(a.title));
})();

/** Every LJ story, Latest first — the pool the home page's LATEST section
 *  reveals into as the reader presses View More. */
export const LATEST_POOL: LjArticle[] = [
  ...LATEST_ARTICLES,
  ...RECOMMENDED_ARTICLES,
  ...POPULAR_ARTICLES,
  HERO_ARTICLE,
].filter((a, i, all) => all.findIndex((b) => b.title === a.title) === i);

/* ── Result ordering ─────────────────────────────────────────────────────── */

export type LjSearchSort = "relevance" | "newest" | "oldest";

export const LJ_SEARCH_SORTS: { value: LjSearchSort; label: string }[] = [
  { value: "relevance", label: "Relevance" },
  { value: "newest", label: "Newest" },
  { value: "oldest", label: "Oldest" },
];

/**
 * Recency, without inventing timestamps.
 *
 * No LJ fixture carries a date, and stamping one on each story to make a sort
 * control work would be making the data up. `POOL` is already ordered
 * newest-first — LATEST, then RECOMMENDED, then POPULAR — so position in it IS
 * the recency the rest of the magazine renders by. Newest reads it forwards,
 * oldest backwards.
 */
const RECENCY = new Map(POOL.map((a, i) => [a.title, i]));

/**
 * A publication date for a result row.
 *
 * QA: "article items: add date to articles". No LJ fixture carries a
 * timestamp, and the sort control above the list already treats POSITION in
 * `POOL` as recency (see `RECENCY`) — so the dates are derived from that same
 * order rather than invented independently, counting back three days per story
 * from the current issue's date. The date a row shows and the order 「Newest」
 * puts it in therefore always agree, which is the property that matters here;
 * swap this for a real `publishedAt` when the API lands.
 */
const ISSUE_DATE = new Date("2026-02-06T00:00:00Z");

export function ljArticleDate(title: string): string {
  const rank = RECENCY.get(title);
  if (rank === undefined) return "";
  const d = new Date(ISSUE_DATE.getTime() - rank * 3 * 24 * 60 * 60 * 1000);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getUTCFullYear()}.${pad(d.getUTCMonth() + 1)}.${pad(d.getUTCDate())}`;
}

/**
 * How well a story answers the query.
 *
 * A title hit beats a category hit beats a byline hit, and a hit at the start
 * of the title beats one buried in it — otherwise "relevance" is just the
 * fixture order under a different name.
 */
function score(a: LjArticle, q: string): number {
  const title = a.title.toLowerCase();
  const cat = a.category.toLowerCase();
  const author = (a.author ?? "").toLowerCase();
  let s = 0;
  if (title.startsWith(q)) s += 100;
  else if (title.includes(q)) s += 60;
  if (cat === q) s += 40;
  else if (cat.includes(q)) s += 20;
  if (author.includes(q)) s += 15;
  return s;
}

export function ljSearch(
  query: string,
  sort: LjSearchSort = "relevance",
): LjArticle[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];
  const hits = POOL.filter((a) =>
    `${a.title} ${a.category} ${a.author ?? ""}`.toLowerCase().includes(q),
  );
  const age = (a: LjArticle) => RECENCY.get(a.title) ?? Number.MAX_SAFE_INTEGER;
  if (sort === "newest") return [...hits].sort((a, b) => age(a) - age(b));
  if (sort === "oldest") return [...hits].sort((a, b) => age(b) - age(a));
  return [...hits].sort((a, b) => score(b, q) - score(a, q) || age(a) - age(b));
}

/**
 * Type-ahead over what the magazine actually holds.
 *
 * The field offered nothing until you pressed Return, so a reader had to guess
 * a spelling the fixtures use. QA: "add auto suggestion when added text in
 * search bar". Titles first, then the categories and popular terms that match —
 * capped at six, because a list longer than that covers the results behind it.
 */
export function ljSuggest(query: string, limit = 6): string[] {
  const q = query.trim().toLowerCase();
  if (q.length < 1) return [];
  const out: string[] = [];
  const push = (v: string) => {
    if (out.length < limit && !out.some((x) => x.toLowerCase() === v.toLowerCase()))
      out.push(v);
  };
  POOL.filter((a) => a.title.toLowerCase().includes(q)).forEach((a) =>
    push(a.title),
  );
  LJ_CATEGORIES.filter((c) => c.label.toLowerCase().includes(q)).forEach((c) =>
    push(c.label),
  );
  LJ_POPULAR_SEARCHES.filter((t) => t.toLowerCase().includes(q)).forEach(push);
  POOL.map((a) => a.author)
    .filter((a): a is string => !!a && a.toLowerCase().includes(q))
    .forEach(push);
  return out;
}

/** The terms offered on the empty search page. */
export const LJ_POPULAR_SEARCHES = [
  "Armani",
  "腕錶",
  "米芝蓮",
  "展覽",
  "Marcus Lau",
  "旅遊",
];

export const LJ_SEARCH_CATEGORIES = LJ_CATEGORIES;
